import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Ornament } from '@/components/generals/ornament'

interface SectionHeadingProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    align?: 'center' | 'left'
    tone?: 'light' | 'dark'
    as?: 'h1' | 'h2'
    id?: string
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'center',
    tone = 'light',
    as: Heading = 'h2',
    id,
    className
}): ReactNode => {
    const isCenter = align === 'center'
    const isDark = tone === 'dark'

    return (
        <div className={cn('max-w-2xl', isCenter && 'mx-auto text-center', className)}>
            <p
                className={cn(
                    'flex items-center gap-3 text-xs font-medium uppercase tracking-[0.32em]',
                    isCenter && 'justify-center',
                    isDark ? 'text-gold-light' : 'text-gold-dark'
                )}
            >
                {eyebrow}
            </p>

            <Heading
                id={id}
                className={cn(
                    'mt-4 font-heading text-[1.75rem] font-semibold leading-tight sm:text-4xl lg:text-[2.6rem]',
                    isDark ? 'text-ivory' : 'text-plum'
                )}
            >
                {title}
            </Heading>

            <Ornament className={cn('mt-5', isCenter ? 'mx-auto' : '')} />

            {description && (
                <p
                    className={cn(
                        'mt-5 text-base leading-relaxed sm:text-lg',
                        isDark ? 'text-ivory/75' : 'text-muted-foreground'
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    )
}
